import { useMemo } from "react";

import { useQuery } from "@apollo/client";

import { RETRY_TRANSIENT } from "../../apollo/provider/createTransientRetryLink";
import { TopPerformerLensStatus as ApiStatus } from "../../gql-generated/types";
import { useSelectedOrganizationContainer } from "../../shared/hooks";
import {
    type TopPerformerLensesQuery,
    type TopPerformerLensesQueryVariables,
    getTopPerformerLenses,
} from "../api/queries";
import { type TopPerformerLensRow, TopPerformerLensStatus } from "../models";

const STATUS_BY_API_STATUS: Record<ApiStatus, TopPerformerLensStatus> = {
    [ApiStatus.Awarded]: TopPerformerLensStatus.Awarded,
    [ApiStatus.DidNotQualify]: TopPerformerLensStatus.DidNotQualify,
    [ApiStatus.InWindow]: TopPerformerLensStatus.InWindow,
    [ApiStatus.UnderReview]: TopPerformerLensStatus.UnderReview,
};

interface UseTopPerformerLensesResult {
    loading: boolean;
    error?: Error;
    lenses: TopPerformerLensRow[];
    enrolledCount: number;
    awardedCount: number;
    totalEarnedMilliUsd: number;
}

export const useTopPerformerLenses = (): UseTopPerformerLensesResult => {
    const { lensFolderId } = useSelectedOrganizationContainer();
    const { data, loading, error } = useQuery<TopPerformerLensesQuery, TopPerformerLensesQueryVariables>(
        getTopPerformerLenses,
        { variables: { input: { lensSpaceId: lensFolderId } }, context: RETRY_TRANSIENT },
    );
    const response = data?.topPerformerLenses;
    // VirtualTable needs a mutable array with a stable identity across renders.
    const lenses = useMemo<TopPerformerLensRow[]>(
        () =>
            (response?.lenses ?? [])
                .map(({ lens, status, qualifiedPosters, postersThreshold, award }) => ({
                    lens: {
                        id: lens.id,
                        name: lens.name,
                        iconUrl: lens.iconUrl,
                        organizationId: lens.organizationId,
                        lensAccountId: lens.lensAccountId ? { lensSpaceId: lens.lensAccountId.lensSpaceId } : null,
                    },
                    status: Object.prototype.hasOwnProperty.call(STATUS_BY_API_STATUS, status)
                        ? STATUS_BY_API_STATUS[status]
                        : TopPerformerLensStatus.Unknown,
                    qualifiedPosters: qualifiedPosters ?? null,
                    postersThreshold,
                    award: award
                        ? {
                              amountMilliUsd: award.amountMilliUsd,
                              awardedMonth: award.awardedMonth,
                              paidOn: award.paidOn,
                          }
                        : null,
                }))
                .sort((firstLens, secondLens) => Number(secondLens.award !== null) - Number(firstLens.award !== null)),
        [response?.lenses],
    );

    return {
        loading,
        error,
        lenses,
        enrolledCount: response?.enrolledCount ?? 0,
        awardedCount: response?.awardedCount ?? 0,
        totalEarnedMilliUsd: response?.totalEarnedMilliUsd ?? 0,
    };
};
